import { lazy, Suspense } from "react";
import { createBrowserRouter, type RouteObject } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { SectionLoader } from "@/components/ui/Feedback";
import RouteError from "@/pages/RouteError";
import HomePage from "@/pages/Home";
import { AdminLayout } from "@/admin/AdminLayout";
import { AdminRoute } from "@/admin/AdminRoute";

/**
 * Home is eager — it is the most likely entry point, so it should not wait on a
 * second network round-trip. Everything else is split.
 */
const CompletedProjects = lazy(() => import("@/pages/CompletedProjects"));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));
const TurnkeyProjects = lazy(() => import("@/pages/TurnkeyProjects"));
const TurnkeyDetail = lazy(() => import("@/pages/TurnkeyDetail"));
const FloorPlans = lazy(() => import("@/pages/FloorPlans"));
const FloorPlanDetail = lazy(() => import("@/pages/FloorPlanDetail"));
const Services = lazy(() => import("@/pages/Services"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const About = lazy(() => import("@/pages/About"));
const Journal = lazy(() => import("@/pages/Journal"));
const ArticleDetail = lazy(() => import("@/pages/ArticleDetail"));
const Careers = lazy(() => import("@/pages/Careers"));
const JobDetail = lazy(() => import("@/pages/JobDetail"));
const Contact = lazy(() => import("@/pages/Contact"));
const Search = lazy(() => import("@/pages/Search"));
const NotFound = lazy(() => import("@/pages/NotFound"));

/* The content console. Lazy as a block — a visitor reading the journal should
   never download the CMS, and none of it renders without a token anyway. */
const AdminLogin = lazy(() => import("@/admin/pages/Login"));
const AdminDashboard = lazy(() => import("@/admin/pages/Dashboard"));
const ContentList = lazy(() => import("@/admin/pages/ContentList"));
const ContentEditor = lazy(() => import("@/admin/pages/ContentEditor"));
const MediaLibrary = lazy(() => import("@/admin/pages/MediaLibrary"));
const AdminSettings = lazy(() => import("@/admin/pages/Settings"));

function page(element: React.ReactNode) {
  return <Suspense fallback={<SectionLoader />}>{element}</Suspense>;
}

const routes: RouteObject[] = [
  {
    path: "/",
    element: <Layout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <HomePage /> },

      { path: "projects", element: page(<CompletedProjects />) },
      { path: "projects/:id", element: page(<ProjectDetail />) },

      { path: "turnkey", element: page(<TurnkeyProjects />) },
      { path: "turnkey/:id", element: page(<TurnkeyDetail />) },

      { path: "floor-plans", element: page(<FloorPlans />) },
      { path: "floor-plans/:id", element: page(<FloorPlanDetail />) },

      { path: "services", element: page(<Services />) },
      { path: "services/:id", element: page(<ServiceDetail />) },

      { path: "about", element: page(<About />) },

      { path: "journal", element: page(<Journal />) },
      { path: "journal/:id", element: page(<ArticleDetail />) },

      { path: "careers", element: page(<Careers />) },
      { path: "careers/:id", element: page(<JobDetail />) },

      { path: "contact", element: page(<Contact />) },
      { path: "search", element: page(<Search />) },

      { path: "*", element: page(<NotFound />) },
    ],
  },

  /* The console sits outside the public Layout: it has its own shell, and the
     site header and footer would be noise around a data table. Login is outside
     the guard for the obvious reason. */
  { path: "/admin/login", element: page(<AdminLogin />), errorElement: <RouteError /> },
  {
    path: "/admin",
    element: (
      <AdminRoute>
        <AdminLayout />
      </AdminRoute>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: page(<AdminDashboard />) },
      { path: "content/:entityType", element: page(<ContentList />) },
      { path: "content/:entityType/:id", element: page(<ContentEditor />) },
      { path: "media", element: page(<MediaLibrary />) },
      { path: "settings", element: page(<AdminSettings />) },
    ],
  },
];

export const router = createBrowserRouter(routes);
export { routes };
